import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Download, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { useMeetingAgenda } from './use-meeting-agenda';
import { MeetingAgendaModalProps } from './meeting-agenda.types';
import { useTenancy } from '@/contexts/TenancyContext';

export function MeetingAgendaModal({
  isOpen,
  onClose,
}: MeetingAgendaModalProps) {
  const [exportSuccess, setExportSuccess] = useState<boolean | null>(null);
  const [exportFormat, setExportFormat] = useState<'word' | 'pdf'>('word');
  const { activeTenancy, userIdToName } = useTenancy();
  const dialogRef = useRef<HTMLDivElement>(null);
  
  const {
    assuranceItems,
    isLoading,
    error,
    reviewPeriod,
    setReviewPeriod,
    exportAgenda,
    isExporting,
    refresh,
  } = useMeetingAgenda();
  
  // Check if any task ID is considered long (more than 10 characters)
  const hasLongTaskIds = useMemo(() => {
    return assuranceItems.some(item => item.item.assID?.length > 10);
  }, [assuranceItems]);
  
  const taskIdColumnWidth = hasLongTaskIds ? 'w-32' : 'w-24';
  const gapSize = hasLongTaskIds ? 'gap-4' : 'gap-2';

  useEffect(() => {
    if (exportSuccess !== null) {
      const timer = setTimeout(() => setExportSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [exportSuccess]);

  const handleExport = async (format: 'word' | 'pdf') => {
    if (!activeTenancy?.id) {
      toast.error('No active tenancy found');
      return;
    }

    setExportSuccess(null);

    try {
      const success = await exportAgenda(format);
      setExportSuccess(success);
      
      if (success) {
        toast.success(`Agenda exported successfully as ${format.toUpperCase()}`);
        setTimeout(() => setExportSuccess(null), 3000);
      } else {
        toast.error('Failed to export agenda');
      }
    } catch (error) {
      console.error('Error exporting agenda:', error);
      setExportSuccess(false);
      toast.error('An error occurred while exporting the agenda');
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'bg-gray-100 dark:bg-gray-700';

    switch (status.toLowerCase()) {
      case 'complete':
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'not started':
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-6xl max-w-6xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-secondary dark:bg-background" ref={dialogRef}>
        <DialogHeader className="border-b p-6">
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-center">
              <DialogTitle className="text-xl">Meeting Agenda</DialogTitle>
              <div className="flex items-center space-x-2 mr-3">
              <Select
                value={reviewPeriod}
                onValueChange={(value) => {
                  // Stop event propagation to prevent dialog from closing
                  setReviewPeriod(value as '3' | '6' | '12');
                }}
                disabled={isLoading}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Review Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 Months</SelectItem>
                  <SelectItem value="6">Last 6 Months</SelectItem>
                  <SelectItem value="12">Last 12 Months</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={exportFormat}
                onValueChange={(value) => setExportFormat(value as 'word' | 'pdf')}
                disabled={isExporting || isLoading || assuranceItems.length === 0}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Export as..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="word">Export as Word</SelectItem>
                  {/* <SelectItem value="pdf">Export as PDF</SelectItem> */}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => refresh()}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Refresh</span>
              </Button>

              <Button
                onClick={() => handleExport(exportFormat)}
                disabled={isExporting || isLoading || assuranceItems.length === 0}
                className="flex items-center gap-2"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>{isExporting ? 'Exporting...' : exportSuccess ? 'Exported' : `Export as ${exportFormat.toUpperCase()}`}</span>
              </Button>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden" ref={dialogRef}>
          {error ? (
            <Card className="border-destructive m-4">
              <CardContent className="p-4 flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error loading meeting agenda</p>
                  <p className="text-sm">{error.message}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[calc(90vh-200px)] px-6 py-4">
              <div className="space-y-6 pr-4">
                <section>
                  <h3 className="text-lg font-semibold mb-3">Meeting Agenda</h3>
                  <div className="space-y-2 pl-5 border-l-2 border-primary/20">
                    <p>All aspects of the management system should be reviewed by management at planned intervals, and at least yearly - by setting up suitable schedules and agenda items in management meetings. New or less mature management systems should be reviewed more frequently by management to drive increased effectiveness.</p>
                    <p>Documented information from management reviews is required. It should be retained to demonstrate that consideration has been given to (at least) all the areas listed in Management System, even where it is decided that no action is necessary.</p>
                    <p>Export the meeting agenda from the menu above and use this to run your management meeting. attach the completed document when performing the committee meeting assurance task, to ensure your documented evidence is available during future audit and review cycles.“</p>
                  </div>
                </section>

                <section>
                <h4 className="font-medium mb-4">Review Assurance Items</h4>
                    {isLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : assuranceItems.length > 0 ? (
                      <div className="space-y-3">
                        {assuranceItems.map((item, index) => (
                          <div key={`${item.taskID}-${index}`} className={`flex ${gapSize} p-4 rounded-md border border-border/50 hover:bg-muted/30 transition-colors`}>
                            <div className={`${taskIdColumnWidth} flex-shrink-0`}>
                              <span className="text-muted-foreground text-sm font-mono break-words inline-block w-full">
                                {item.item.assID}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground break-words">{item.name}</p>
                              <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-muted-foreground">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(item.item.checkDetail.active ? "active" : "inactive")}`}>
                                  {item.item.checkDetail.active ? "Active" : "Inactive"}
                                </span>
                                {item.item.assignedTo && (
                                  <span className="flex items-center gap-1 flex-nowrap">
                                    <span className="text-muted-foreground/60 whitespace-nowrap">Assigned to</span>
                                    <span className="font-medium text-foreground/90 truncate max-w-[120px] sm:max-w-none">{userIdToName[item.item.assignedTo]}</span>
                                  </span>
                                )}
                                {item.nextCheck && (
                                  <span className="flex items-center gap-1 flex-nowrap">
                                    <span className="text-muted-foreground/60 whitespace-nowrap">Due</span>
                                    <span className="font-medium text-foreground/90">{formatDate(item.nextCheck)}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No assurance items found for the selected period.</p>
                    )}
                </section>
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
